import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { Container, Card, CardBody, Row, Col, Input, Button, Badge } from 'reactstrap';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-alpine.css';
import 'ag-grid-community/dist/styles/ag-theme-alpine-dark.css';
import { themeTypeState } from '../../../state/GlobalState';
import { getActivePermitsRequest } from '../../../api/controller/ActivePermitsController';
import { activePermitsListState, activePermitsSearchState } from './state';
import { Loader } from '../../../custom/components/Loader';

const ActivePermits = () => {
    const [dataList, setDataList] = useRecoilState(activePermitsListState);
    const [search, setSearch] = useRecoilState(activePermitsSearchState);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('active');
    const themeType = useRecoilValue(themeTypeState);
    const gridApiRef = useRef(null);

    const fetchData = useCallback(async (q, type = 'active') => {
        setLoading(true);
        const res = await getActivePermitsRequest({ SearchQuery: q, Type: type });
        setLoading(false);
        if (res && res.Status !== 'error') {
            setDataList(res.data ?? []);
        }
    }, [setDataList]);

    useEffect(() => {
        fetchData('', activeTab);
        return () => setDataList([]);
        // eslint-disable-next-line
    }, []);

    const columnDefs = [
        {
            headerName: 'Vessel',
            valueGetter: p => p.data?.vessel?.name ?? '-',
            minWidth: 160,
            flex: 2,
        },
        {
            headerName: 'Reg. No.',
            valueGetter: p => p.data?.vessel?.registration_number ?? '-',
            minWidth: 130,
            flex: 1,
        },
        {
            headerName: 'Owner',
            valueGetter: p => p.data?.vessel?.owner?.name ?? '-',
            minWidth: 140,
            flex: 1,
        },
        {
            headerName: 'Company',
            valueGetter: p => p.data?.vessel?.owner_company_name ?? '-',
            minWidth: 160,
            flex: 1,
        },
        {
            headerName: 'Permit Type',
            valueGetter: p => p.data?.permit_type?.name ?? '-',
            minWidth: 120,
            flex: 1,
        },
        {
            headerName: 'Effective',
            field: 'permit_effective_display',
            minWidth: 160,
            flex: 1,
        },
        {
            headerName: 'Expires',
            field: 'permit_expiry_display',
            minWidth: 160,
            flex: 1,
        },
    ];

    const defaultColDef = { sortable: true, resizable: true, filter: true };

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <h4 className="mb-4">
                        Active Permits
                        {dataList.length > 0 && (
                            <Badge color="primary" className="ms-2 fs-6 align-middle">{dataList.length}</Badge>
                        )}
                    </h4>

                    <div className="mb-3">
                        <button
                            className={`btn me-2 ${activeTab === 'active' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => { setActiveTab('active'); fetchData(search, 'active'); }}
                        >
                            Active
                        </button>
                        <button
                            className={`btn ${activeTab === 'upcoming' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => { setActiveTab('upcoming'); fetchData(search, 'upcoming'); }}
                        >
                            Upcoming
                        </button>
                    </div>

                    <Card className="mb-3">
                        <CardBody>
                            <Row className="align-items-center">
                                <Col md={5}>
                                    <Input
                                        placeholder="Search by vessel name or registration number..."
                                        value={search}
                                        onChange={e => setSearch(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && fetchData(search, activeTab)}
                                    />
                                </Col>
                                <Col md="auto" className="mt-2 mt-md-0">
                                    <Button color="primary" onClick={() => fetchData(search, activeTab)} disabled={loading}>
                                        {loading ? 'Loading...' : 'Search'}
                                    </Button>
                                    <Button color="secondary" className="ms-2" onClick={() => { setSearch(''); fetchData('', activeTab); }} disabled={loading}>
                                        Clear
                                    </Button>
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>

                    {loading ? <Loader /> : (
                        <Card>
                            <CardBody className="p-0">
                                <div
                                    className={themeType === 'dark' ? 'ag-theme-alpine-dark' : 'ag-theme-alpine'}
                                    style={{ height: 500, width: '100%' }}
                                >
                                    <AgGridReact
                                        ref={gridApiRef}
                                        rowData={dataList}
                                        columnDefs={columnDefs}
                                        defaultColDef={defaultColDef}
                                        pagination
                                        paginationPageSize={20}
                                        suppressCellFocus
                                    />
                                </div>
                            </CardBody>
                        </Card>
                    )}
                </Container>
            </div>
        </React.Fragment>
    );
};

export default ActivePermits;
